
import React, { useEffect, useState } from 'react';
import { useMapboxToken } from '@/contexts/MapboxTokenContext';
import SecurityMap from './SecurityMap';
import MapboxTokenForm from './MapboxTokenForm';
import { toast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';

const SecurityMapWrapper = () => {
  const { mapboxToken } = useMapboxToken();
  const [isLoading, setIsLoading] = useState(true);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!mapboxToken) {
      setIsLoading(false);
      return;
    }
    
    console.log("Validating Mapbox token:", mapboxToken.substring(0, 10) + "...");
    setIsLoading(true);
    
    // Use a simple image request for validation instead of the API endpoint
    // This avoids CORS issues and is more reliable
    const img = new Image();
    const timestamp = Date.now();
    img.src = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/0,0,1/1x1?access_token=${mapboxToken}&cacheBuster=${timestamp}`;
    
    img.onload = () => {
      console.log("Token validation successful");
      setTokenValidated(true);
      setValidationError(null);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      console.error("Token validation failed");
      setTokenValidated(false);
      setValidationError("Invalid or expired Mapbox token");
      setIsLoading(false);
      toast({
        title: "Token Validation Failed",
        description: "The provided Mapbox token is invalid or expired.",
        variant: "destructive",
      });
    };
  }, [mapboxToken]);
  
  useEffect(() => {
    if (!mapboxToken) {
      toast({
        title: "Mapbox token required",
        description: "Please add your Mapbox token to view the security map.",
        variant: "destructive",
      });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="h-full w-full bg-black flex items-center justify-center"> 
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
          <p className="text-muted-foreground text-sm">Validating Mapbox token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-black"> 
      {!mapboxToken ? (
        <MapboxTokenForm />
      ) : !tokenValidated ? (
        <div className="h-full w-full flex items-center justify-center">
          <div className="bg-background/80 backdrop-blur-sm p-6 rounded-lg shadow-lg border text-center max-w-md">
            <p className="text-destructive font-medium mb-2">Mapbox Token Error</p>
            <p className="text-muted-foreground mb-4">{validationError || "The token could not be validated."}</p>
            <MapboxTokenForm />
          </div>
        </div>
      ) : (
        <SecurityMap mapboxToken={mapboxToken} />
      )}
    </div>
  );
};

export default SecurityMapWrapper;
